import React, { useState, useEffect, useMemo } from 'react';
import { db, paths } from '../../firebase/config';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { 
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList, Label 
} from 'recharts';

// --- ÍCONES ---
const FilterIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon></svg>;
const UsersIcon = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>;
const AlertIcon = (props) => <svg {...props} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>;
const CheckCircleIcon = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>;
const MapPinIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>;
const CopyIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>;

// --- CORES DO TEMA ---
const COLORS = {
  primary: '#3182ce',    
  success: '#38a169',    
  warning: '#dd6b20',    
  danger: '#e53e3e',     
  purple: '#805ad5',     
  gray: '#718096',       
};

const PIE_COLORS = ['#3182ce', '#38a169', '#805ad5', '#e53e3e', '#dd6b20', '#319795', '#d69e2e', '#e53e3e'];

// --- ESTILOS INTERATIVOS ---
const interactiveStyles = `
  .interactive-card { transition: transform 0.2s ease, box-shadow 0.2s ease; }
  .interactive-card:hover { transform: translateY(-2px); box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06) !important; }
  .table-row-hover { transition: background-color 0.1s; }
  .table-row-hover:hover { background-color: #f7fafc !important; }
  .filter-btn { transition: all 0.2s ease; }
  .filter-btn:hover:not(.active) { background-color: #edf2f7 !important; }
`;

// --- UTILITÁRIOS ---
const getTodayDate = () => new Date().toISOString().split('T')[0];
const formatDateBR = (date) => !date ? '-' : date.split('-').reverse().join('/');

const normalize = (str) => {
    if (!str) return 'ND';
    return String(str)
        .toUpperCase() 
        .replace(/["']/g, "") 
        .replace(/[°ºª]/g, "") 
        .replace(/[^A-Z0-9]/g, "") 
        .trim();
};

const thStyle = { textAlign: 'left', padding: '6px 4px', color: '#a0aec0', fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #e2e8f0' };

const Badge = ({ children, tipo }) => {
    const isDetalhado = tipo === 'DETALHADO';
    return (
        <span style={{ fontSize: '10px', fontWeight: '800', padding: '2px 6px', borderRadius: '12px', backgroundColor: isDetalhado ? '#fff5f5' : '#ebf8ff', color: isDetalhado ? '#c53030' : '#2b6cb0', border: `1px solid ${isDetalhado ? '#fed7d7' : '#bee3f8'}`, letterSpacing: '0.5px' }}>
            {children}
        </span>
    );
};

// --- COMPONENTE BOTÃO COPIAR DADOS DA CIA ---
const BotaoCopiarCia = ({ cia, configDestinos }) => {
    const [copiado, setCopiado] = useState(false);

    const handleCopiar = () => {
        let texto = `*${cia.nome}*\n\n`;
        let totalEfetivoCia = 0;
        let totalAusentesCia = 0;
        
        const pelotoesOrdenados = Object.values(cia.pelotoes)
            .sort((a,b) => a.nome.localeCompare(b.nome, 'pt-BR', { numeric: true }));

        const destinosSimplesPermitidos = [
            'HPM - CONSULTA/EXAME', 
            'HPM - IAS', 
            'UIS - IAS', 
            'UIS - MED/DENT PS'
        ];

        const isRegistroPermitido = (reg) => {
            const isComplexo = configDestinos?.complexos?.includes(reg.destino);
            const isSimplesPermitido = destinosSimplesPermitidos.includes(reg.destino);
            return isComplexo || isSimplesPermitido;
        };

        pelotoesOrdenados.forEach(pel => {
            totalEfetivoCia += pel.efetivoTotal;
            totalAusentesCia += pel.registros.length;
        });
        const totalPresentesCia = totalEfetivoCia - totalAusentesCia;
        
        texto += `Efetivo Total: ${totalEfetivoCia} | Presentes: ${totalPresentesCia} | Ausentes: ${totalAusentesCia}\n\n`;

        pelotoesOrdenados.forEach(pel => {
            const ausentesPel = pel.registros.length;
            const presentesPel = pel.efetivoTotal - ausentesPel;
            
            texto += `*${pel.nome}*\n`;
            texto += `Ef: ${pel.efetivoTotal} | Pr: ${presentesPel} | Aus: ${ausentesPel}\n`;
            
            const registrosFiltrados = pel.registros.filter(isRegistroPermitido);

            if (ausentesPel === 0) {
                texto += `(Sem alterações)\n\n`;
            } else if (registrosFiltrados.length === 0) {
                texto += `(Nenhum registro detalhado ou médico para listar)\n\n`;
            } else {
                registrosFiltrados.forEach(reg => {
                    const motivo = reg.qru_motivo || reg.informacoes_complementares || '';
                    const foraAula = reg.isForaDaAula ? ' [FORA DE AULA]' : '';
                    const obs = motivo ? ` - ${motivo}` : '';
                    texto += `- ${reg.cadeteNome}: ${reg.destino}${obs}${foraAula}\n`;
                });
                texto += `\n`;
            }
        });

        navigator.clipboard.writeText(texto.trim()).then(() => {
            setCopiado(true);
            setTimeout(() => setCopiado(false), 2000);
        });
    };

    return (
        <button 
            onClick={handleCopiar}
            title={`Copiar registros filtrados da ${cia.nome}`}
            style={{ 
                backgroundColor: copiado ? '#38a169' : '#3182ce', color: 'white', border: 'none', 
                padding: '6px 12px', borderRadius: '6px', fontSize: '11px', 
                fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px',
                transition: 'all 0.2s ease', outline: 'none',
                boxShadow: copiado ? '0 0 0 2px #c6f6d5' : 'none'
            }}
        >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
            {copiado ? 'Copiado!' : 'Copiar Registros'}
        </button>
    );
};

// --- COMPONENTE ACORDEÃO PARA O PELOTÃO ---
const AccordionPelotao = ({ pel, configDestinos }) => {
    const [isOpen, setIsOpen] = useState(false);
    
    // Cálculos de efetivo
    const totalBaixas = pel.registros.length;
    const totalPresentes = pel.efetivoTotal - totalBaixas;
    
    return (
        <div style={{ marginBottom: '6px', border: '1px solid #e2e8f0', borderRadius: '6px', overflow: 'hidden', backgroundColor: 'white' }}>
            <div 
                onClick={() => setIsOpen(!isOpen)}
                style={{ 
                    padding: '8px 12px', backgroundColor: '#f0f4f8', display: 'flex', 
                    justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', 
                    transition: 'background-color 0.2s' 
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '13px', fontWeight: '700', color: '#2d3748' }}>{pel.nome}</span>
                    
                    <span style={{ fontSize: '11px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        
                        {/* 1. TOTAL DE CADETES DO PELOTÃO */}
                        <span style={{ color: '#2b6cb0', backgroundColor: '#ebf8ff', padding: '2px 6px', borderRadius: '4px' }}>
                            {pel.efetivoTotal} Cadetes
                        </span>
                        
                        {/* 2. TOTAL DE AUSENTES (Só mostra se for maior que zero) */}
                        {totalBaixas > 0 && (
                            <>
                                <span style={{ color: '#a0aec0' }}>|</span>
                                <span style={{ color: '#dd6b20', backgroundColor: '#fffaf0', padding: '2px 6px', borderRadius: '4px', border: '1px solid #feebc8' }}>
                                    {totalBaixas} ausente{totalBaixas !== 1 ? 's' : ''}
                                </span>
                            </>
                        )}
                        
                        {/* 3. TOTAL DE PRESENTES */}
                        <span style={{ color: '#a0aec0' }}>|</span>
                        <span style={{ color: '#38a169', backgroundColor: '#f0fff4', padding: '2px 6px', borderRadius: '4px', border: '1px solid #c6f6d5' }}>
                            {totalPresentes} presente{totalPresentes !== 1 ? 's' : ''}
                        </span>
                        
                    </span>
                </div>
                <span style={{ fontWeight: 'bold', color: '#4a5568', fontSize: '16px', lineHeight: '1' }}>
                    {isOpen ? '-' : '+'}
                </span>
            </div>

            {isOpen && totalBaixas > 0 && (
                <div style={{ padding: '8px 12px 12px 12px', backgroundColor: '#fff' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                        <thead>
                            <tr>
                                <th style={thStyle}>Cadete</th>
                                <th style={thStyle}>Destino</th>
                                <th style={thStyle}>Motivo / Obs</th>
                                <th style={thStyle}>Período</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pel.registros.map(reg => (
                                <tr key={reg.id} className="table-row-hover" style={{ borderBottom: '1px solid #f1f5f9' }}>
                                    <td style={{ padding: '6px 4px', fontWeight: '600', color: '#2d3748' }}>{reg.cadeteNome}</td>
                                    <td style={{ padding: '6px 4px' }}>
                                        <Badge tipo={configDestinos.complexos.includes(reg.destino) ? 'DETALHADO' : 'SIMPLES'}>{reg.destino}</Badge>
                                    </td>
                                    <td style={{ padding: '6px 4px', color: '#4a5568' }}>
                                        {reg.qru_motivo || reg.informacoes_complementares || '-'}
                                        {reg.isForaDaAula && <span style={{color:COLORS.danger, fontWeight:'bold', display:'inline-block', fontSize:'9px', marginLeft: '6px', backgroundColor: '#fff5f5', padding: '2px 4px', borderRadius: '4px'}}>FORA DE AULA</span>}
                                    </td>
                                    <td style={{ padding: '6px 4px', color: '#718096' }}>
                                        {formatDateBR(reg.periodo_inicio)} - {formatDateBR(reg.periodo_fim)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

const ResultadoOnlineDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [dataReferencia, setDataReferencia] = useState(getTodayDate());
  
  const [registros, setRegistros] = useState([]);
  const [mapaPelotaoCia, setMapaPelotaoCia] = useState({}); 
  const [contagemEfetivo, setContagemEfetivo] = useState({}); 
  const [nomesOriginais, setNomesOriginais] = useState({}); 
  const [configDestinos, setConfigDestinos] = useState({ simples: [], complexos: [] });

  const [usuariosProblematicos, setUsuariosProblematicos] = useState([]);

  const [filtroCia, setFiltroCia] = useState('TODAS');
  const [filtroPelotao, setFiltroPelotao] = useState('TODOS'); 
  const [filtroTipo, setFiltroTipo] = useState('TODOS'); 
  const [filtroDestinoExato, setFiltroDestinoExato] = useState('TODOS'); 

  // Resetar o filtro de Pelotão sempre que mudar a Companhia
  useEffect(() => {
    setFiltroPelotao('TODOS');
  }, [filtroCia]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const configRef = doc(db, 'artifacts', 'app-estagios', 'public', 'data', 'config_geral', 'destinos_opcoes');
        const configSnap = await getDoc(configRef);
        if (configSnap.exists()) {
            setConfigDestinos({ simples: configSnap.data().simples || [], complexos: configSnap.data().complexos || [] });
        }

        let companhiasSnap = await getDocs(collection(db, 'artifacts', 'app-estagios', 'public', 'data', 'companhias'));
        if (companhiasSnap.empty) companhiasSnap = await getDocs(collection(db, 'companhias'));
        
        const mapCiaIdToName = {};
        companhiasSnap.forEach(doc => {
            const data = doc.data();
            if (data.id) mapCiaIdToName[data.id] = data.nome;
            mapCiaIdToName[doc.id] = data.nome; 
        });

        let pelotoesSnap = await getDocs(collection(db, 'artifacts', 'app-estagios', 'public', 'data', 'pelotoes'));
        if (pelotoesSnap.empty) pelotoesSnap = await getDocs(collection(db, 'pelotoes'));
        
        const mapPelotaoToCia = {};
        const originaisMap = {}; 
        const mapPelotaoIdToNormName = {}; 

        pelotoesSnap.forEach(docSnap => {
            const data = docSnap.data();
            const nomeCia = mapCiaIdToName[data.companhia_id] || data.companhia_nome || data.companhia || 'Sem Cia Definida';
            
            if (data.nome) {
                const normName = normalize(data.nome);
                mapPelotaoToCia[normName] = nomeCia;
                originaisMap[normName] = data.nome.replace(/["']/g, "").trim(); 
                
                mapPelotaoIdToNormName[docSnap.id] = normName;
                if (data.id) mapPelotaoIdToNormName[data.id] = normName;
            }
        });
        setMapaPelotaoCia(mapPelotaoToCia);

        const usersSnap = await getDocs(collection(db, paths.users)); 
        const countMap = {};
        const problematicosTemp = []; 
        
        usersSnap.forEach(d => {
            const data = d.data();
            const nivelStr = String(data.nivel || '').toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
            const isEstudante = ['cadete', 'cadete_academico', 'escalante', 'aspirante', 'aluno', 'asp'].some(termo => nivelStr.includes(termo));
            
            if (isEstudante) {
                let normName = 'ND';
                const rawName = data.pelotao_nome || data.pelotao || 'Sem Pelotão';
                
                if (data.pelotao_id && mapPelotaoIdToNormName[data.pelotao_id]) {
                    normName = mapPelotaoIdToNormName[data.pelotao_id];
                } else {
                    normName = normalize(rawName);
                }

                if (!mapPelotaoToCia[normName]) {
                    problematicosTemp.push({
                        id: d.id, nome: data.nome || data.nome_completo || 'NOME EM BRANCO', cpf: data.cpf || data.matricula || 'SEM CPF/MATRICULA', pelotao: rawName
                    });
                }
                
                if (!countMap[normName]) countMap[normName] = 0;
                countMap[normName]++;
                
                if (!originaisMap[normName] && normName !== 'ND') {
                    originaisMap[normName] = rawName.replace(/["']/g, "").trim(); 
                }
            }
        });
        
        setContagemEfetivo(countMap);
        setNomesOriginais(originaisMap); 
        setUsuariosProblematicos(problematicosTemp);

        const registrosSnap = await getDocs(collection(db, paths.resultado_online));
        const rawRegistros = registrosSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const registrosValidos = rawRegistros.filter(reg => {
             if (!reg.periodo_inicio || !reg.periodo_fim) return false;
             return reg.periodo_inicio <= dataReferencia && reg.periodo_fim >= dataReferencia;
        });
        
        setRegistros(registrosValidos);
      } catch (error) {
        console.error("ERRO CRÍTICO no Dashboard:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [dataReferencia]);

  const destinosDisponiveis = useMemo(() => {
      const setDest = new Set();
      registros.forEach(r => { if(r.destino) setDest.add(r.destino); });
      return ['TODOS', ...Array.from(setDest).sort()];
  }, [registros]);

  useEffect(() => {
      if (filtroDestinoExato !== 'TODOS' && !destinosDisponiveis.includes(filtroDestinoExato)) setFiltroDestinoExato('TODOS');
  }, [destinosDisponiveis, filtroDestinoExato]);

  const dadosConsolidados = useMemo(() => {
    if (loading) return null;

    let filtrados = registros;
    if (filtroTipo !== 'TODOS') filtrados = filtrados.filter(reg => configDestinos.complexos.includes(reg.destino) ? filtroTipo === 'DETALHADO' : filtroTipo === 'SIMPLES');
    if (filtroDestinoExato !== 'TODOS') filtrados = filtrados.filter(reg => reg.destino === filtroDestinoExato);

    const porCia = {};
    const porDestino = {};
    const porGremioEquipe = {}; 
    const resumoCiaDestino = {}; 
    let totalFora = 0;

    filtrados.forEach(reg => {
        const rawName = reg.pelotao || 'Sem Pelotão';
        const normName = normalize(rawName);
        const nomeCia = mapaPelotaoCia[normName] || 'Outras Unidades';
        const displayPelotao = nomesOriginais[normName] || rawName.replace(/["']/g, "").trim();

        if (filtroCia !== 'TODAS' && nomeCia !== filtroCia) return;
        if (filtroPelotao !== 'TODOS' && displayPelotao !== filtroPelotao) return;

        if (!porCia[nomeCia]) porCia[nomeCia] = { nome: nomeCia, ausencias: 0, pelotoes: {} };
        if (!porCia[nomeCia].pelotoes[displayPelotao]) {
            porCia[nomeCia].pelotoes[displayPelotao] = { nome: displayPelotao, registros: [], efetivoTotal: contagemEfetivo[normName] || 0 };
        }

        porCia[nomeCia].pelotoes[displayPelotao].registros.push(reg);
        porCia[nomeCia].ausencias++;
        if (!porDestino[reg.destino]) porDestino[reg.destino] = 0;
        porDestino[reg.destino]++;
        totalFora++;

        if (!resumoCiaDestino[nomeCia]) resumoCiaDestino[nomeCia] = {};
        const destinoUpper = String(reg.destino || 'NÃO INFORMADO').toUpperCase();
        if (!resumoCiaDestino[nomeCia][destinoUpper]) resumoCiaDestino[nomeCia][destinoUpper] = 0;
        resumoCiaDestino[nomeCia][destinoUpper]++;

        const destinoParaGremios = String(reg.destino || '').toUpperCase();
        if (destinoParaGremios.includes('GRÊMIO') || destinoParaGremios.includes('GREMIO') || destinoParaGremios.includes('EQUIPE')) {
            const motivo = reg.qru_motivo || reg.informacoes_complementares || 'Não Especificado';
            if (!porGremioEquipe[motivo]) porGremioEquipe[motivo] = 0;
            porGremioEquipe[motivo]++;
        }
    });

    if (filtroTipo === 'TODOS' && filtroDestinoExato === 'TODOS') {
        Object.keys(contagemEfetivo).forEach(normName => {
            const nomeCia = mapaPelotaoCia[normName] || 'Outras Unidades';
            
            if (filtroCia !== 'TODAS' && nomeCia !== filtroCia) return;
            
            const displayPelotao = nomesOriginais[normName] || normName;
            
            if (filtroPelotao !== 'TODOS' && displayPelotao !== filtroPelotao) return;

            if (!porCia[nomeCia]) porCia[nomeCia] = { nome: nomeCia, ausencias: 0, pelotoes: {} };
            
            if (!porCia[nomeCia].pelotoes[displayPelotao]) {
                porCia[nomeCia].pelotoes[displayPelotao] = { nome: displayPelotao, registros: [], efetivoTotal: contagemEfetivo[normName] };
            }
        });
    }

    let totalEfetivoGeral = 0;
    Object.keys(contagemEfetivo).forEach(normName => {
        const cia = mapaPelotaoCia[normName] || 'Outras Unidades';
        const displayPelotao = nomesOriginais[normName] || normName;

        const matchCia = filtroCia === 'TODAS' || cia === filtroCia;
        const matchPel = filtroPelotao === 'TODOS' || displayPelotao === filtroPelotao;

        if (matchCia && matchPel) {
            totalEfetivoGeral += contagemEfetivo[normName];
        }
    });

    const graficoCiaOrdenado = Object.values(porCia)
        .map(c => ({ name: c.nome, value: c.ausencias }))
        .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { numeric: true }));

    return { 
        totalFora, totalEfetivo: totalEfetivoGeral, totalEmForma: Math.max(0, totalEfetivoGeral - totalFora), 
        porCia, 
        resumoCiaDestino, 
        graficoCia: graficoCiaOrdenado, 
        graficoDestino: Object.keys(porDestino).map(k => ({ name: k, value: porDestino[k] })).sort((a,b) => b.value - a.value),
        graficoGremioEquipe: Object.keys(porGremioEquipe).map(k => ({ name: k, value: porGremioEquipe[k] })).sort((a,b) => b.value - a.value) 
    };
  }, [registros, mapaPelotaoCia, contagemEfetivo, nomesOriginais, filtroCia, filtroPelotao, filtroTipo, filtroDestinoExato, configDestinos, loading]);

  const listaCias = useMemo(() => {
      const s = new Set(Object.values(mapaPelotaoCia));
      if(dadosConsolidados) Object.keys(dadosConsolidados.porCia).forEach(c => s.add(c));
      return ['TODAS', ...Array.from(s).sort()];
  }, [mapaPelotaoCia, dadosConsolidados]);

  const listaPelotoes = useMemo(() => {
      const s = new Set();
      Object.keys(contagemEfetivo).forEach(normName => {
          const nomeCia = mapaPelotaoCia[normName] || 'Outras Unidades';
          if (filtroCia === 'TODAS' || nomeCia === filtroCia) {
              const display = nomesOriginais[normName] || normName;
              s.add(display);
          }
      });
      return ['TODOS', ...Array.from(s).sort((a, b) => a.localeCompare(b, 'pt-BR', { numeric: true }))];
  }, [contagemEfetivo, mapaPelotaoCia, nomesOriginais, filtroCia]);

  const getCorCompanhia = (nome) => {
      if (nome.includes('1ª')) return '#d69e2e'; 
      if (nome.includes('2ª')) return '#38a169'; 
      if (nome.includes('3ª')) return '#3182ce'; 
      if (nome.includes('4ª')) return '#e53e3e'; 
      return '#718096'; 
  };

  const totalGremiosEquipes = dadosConsolidados?.graficoGremioEquipe ? dadosConsolidados.graficoGremioEquipe.reduce((acc, curr) => acc + curr.value, 0) : 0;

  if (loading) return (
    <div style={{display:'flex', justifyContent:'center', alignItems:'center', height:'80vh', flexDirection:'column', color: COLORS.primary}}>
        <div style={{fontWeight:'bold', fontSize:'18px'}}>Carregando Dashboard...</div>
        <div style={{fontSize:'14px', color: '#718096', marginTop:'8px'}}>Consolidando dados das companhias...</div>
    </div>
  );

  return (
    <div style={{ backgroundColor: '#f0f2f5', minHeight: '100vh', padding: '16px', fontFamily: 'Inter, sans-serif' }}>
      <style>{interactiveStyles}</style>

      {usuariosProblematicos.length > 0 && (
          <div style={{ backgroundColor: '#fff5f5', border: '1px solid #e53e3e', padding: '16px', borderRadius: '8px', marginBottom: '16px' }}>
              <h2 style={{ color: '#c53030', margin: '0 0 8px 0', fontSize: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}><AlertIcon /> AÇÃO NECESSÁRIA NO BANCO DE DADOS</h2>
              <p style={{ color: '#742a2a', fontSize: '13px', margin: '0 0 12px 0' }}>Exclua estes usuários da coleção `users` no Firebase pois eles não possuem Cia vinculada corretamente.</p>
              <div style={{ overflowX: 'auto', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #fed7d7' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '12px' }}>
                      <thead style={{ backgroundColor: '#fed7d7', color: '#9b2c2c' }}>
                          <tr><th style={{ padding: '8px' }}>Nome</th><th style={{ padding: '8px' }}>CPF/Doc</th><th style={{ padding: '8px' }}>Pelotão Defeituoso</th><th style={{ padding: '8px' }}>ID DO BANCO</th></tr>
                      </thead>
                      <tbody>
                          {usuariosProblematicos.map((u, i) => (
                              <tr key={i} style={{ borderBottom: '1px solid #fee2e2' }}>
                                  <td style={{ padding: '8px', fontWeight: 'bold' }}>{u.nome}</td><td style={{ padding: '8px' }}>{u.cpf}</td><td style={{ padding: '8px', color: '#e53e3e' }}>{u.pelotao}</td>
                                  <td style={{ padding: '8px' }}><code style={{ backgroundColor: '#f7fafc', padding: '2px 6px', borderRadius: '4px', color: '#2b6cb0', userSelect: 'all' }}>{u.id}</code></td>
                              </tr>
                          ))}
                      </tbody>
                  </table>
              </div>
          </div>
      )}
      
      {/* 1. CABEÇALHO COMPACTO */}
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px', marginBottom: '16px' }}>
          
          {/* Esquerda: Logo e Título */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <img src="https://apmbb.com.br/resultados/brasao.png" alt="Brasão APMBB" style={{ height: '48px', width: '48px', objectFit: 'contain' }} />
              <div>
                  <h1 style={{ margin: 0, color: '#1a202c', fontSize: '22px', fontWeight: '800', lineHeight: 1.2 }}>Dashboard Operacional</h1>
                  <p style={{ margin: '2px 0 0', color: '#718096', fontSize: '12px' }}>Visão consolidada por Companhia e Pelotão</p>
              </div>
          </div>

          {/* Direita: Filtros e Data */}
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
            
            <div className="interactive-card" style={{ display: 'flex', gap: '8px', alignItems: 'center', background: 'white', padding: '6px 12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#a0aec0' }}>DATA:</span>
                <input type="date" value={dataReferencia} onChange={(e) => setDataReferencia(e.target.value)} style={{ border: 'none', fontWeight: 'bold', color: COLORS.primary, fontSize: '13px', outline: 'none', cursor:'pointer' }}/>
            </div>

            <div style={{ position: 'relative', minWidth: '160px' }}>
                <select value={filtroCia} onChange={(e) => setFiltroCia(e.target.value)} style={{ width: '100%', padding: '8px 30px 8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', appearance: 'none', backgroundColor: 'white', fontWeight: '600', fontSize: '12px', color: '#4a5568', cursor: 'pointer' }}>
                    {listaCias.map(c => <option key={c} value={c}>{c === 'TODAS' ? 'Todas as Cias' : c}</option>)}
                </select>
                <div style={{ position: 'absolute', right: '10px', top: '10px', pointerEvents: 'none', color: '#a0aec0' }}><FilterIcon /></div>
            </div>

            <div style={{ position: 'relative', minWidth: '160px' }}>
                <select value={filtroPelotao} onChange={(e) => setFiltroPelotao(e.target.value)} style={{ width: '100%', padding: '8px 30px 8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', appearance: 'none', backgroundColor: 'white', fontWeight: '600', fontSize: '12px', color: '#4a5568', cursor: 'pointer' }}>
                    {listaPelotoes.map(p => <option key={p} value={p}>{p === 'TODOS' ? 'Todos os Pelotões' : p}</option>)}
                </select>
                <div style={{ position: 'absolute', right: '10px', top: '10px', pointerEvents: 'none', color: '#a0aec0' }}><UsersIcon /></div>
            </div>

            <div style={{ position: 'relative', minWidth: '160px' }}>
                <select value={filtroDestinoExato} onChange={(e) => setFiltroDestinoExato(e.target.value)} style={{ width: '100%', padding: '8px 30px 8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', appearance: 'none', backgroundColor: 'white', fontWeight: '600', fontSize: '12px', color: '#4a5568', cursor: 'pointer' }}>
                    {destinosDisponiveis.map(d => <option key={d} value={d}>{d === 'TODOS' ? 'Todos os Motivos' : d}</option>)}
                </select>
                <div style={{ position: 'absolute', right: '10px', top: '10px', pointerEvents: 'none', color: '#a0aec0' }}><MapPinIcon /></div>
            </div>

            <div style={{ display: 'flex', background: 'white', borderRadius: '8px', padding: '3px', border: '1px solid #e2e8f0' }}>
                {['TODOS', 'SIMPLES', 'DETALHADO'].map(tipo => (
                    <button key={tipo} onClick={() => setFiltroTipo(tipo)} className={`filter-btn ${filtroTipo === tipo ? 'active' : ''}`} style={{ padding: '4px 12px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontWeight: 'bold', fontSize: '11px', backgroundColor: filtroTipo === tipo ? COLORS.primary : 'transparent', color: filtroTipo === tipo ? 'white' : '#718096' }}>
                        {tipo}
                    </button>
                ))}
            </div>
          </div>
      </header>

      {/* 2. KPI CARDS COMPACTOS */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '16px' }}>
        <KPICard title="Novidade" value={dadosConsolidados?.totalFora || 0} color={COLORS.danger} icon={<AlertIcon />} />
        <KPICard title="Pronto" value={dadosConsolidados?.totalEmForma || 0} color={COLORS.success} icon={<CheckCircleIcon />} />
        <KPICard title="Efetivo Total" value={dadosConsolidados?.totalEfetivo || 0} color={COLORS.primary} icon={<UsersIcon />} />
      </div>

      {/* 3. GRÁFICOS REDUZIDOS */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginBottom: '16px' }}>
        
        {/* GRÁFICO BARRAS */}
        <div className="interactive-card" style={{ backgroundColor: 'white', borderRadius: '12px', padding: '16px', border: '1px solid #e2e8f0' }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#2d3748', fontWeight: '700' }}>Ausências por Companhia</h3>
            <div style={{ height: '180px' }}>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={dadosConsolidados?.graficoCia || []} margin={{ top: 20, right: 10, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#edf2f7" />
                        <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fill: '#a0aec0', fontSize: 10}} />
                        <YAxis axisLine={false} tickLine={false} tick={{fill: '#a0aec0', fontSize: 10}} />
                        <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} cursor={{ fill: '#f7fafc' }} />
                        <Bar dataKey="value" radius={[4, 4, 0, 0]} barSize={28}>
                            {dadosConsolidados?.graficoCia.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={getCorCompanhia(entry.name)} />
                            ))}
                            <LabelList dataKey="value" position="top" fill="#4a5568" fontSize={11} fontWeight="bold" />
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>

        {/* GRÁFICO DE PIZZA */}
        <div className="interactive-card" style={{ backgroundColor: 'white', borderRadius: '12px', padding: '16px', border: '1px solid #e2e8f0' }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#2d3748', fontWeight: '700' }}>Motivos</h3>
            <div style={{ height: '180px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie 
                            data={dadosConsolidados?.graficoDestino || []} 
                            cx="50%" cy="50%" 
                            innerRadius={45} outerRadius={70} 
                            paddingAngle={3} 
                            dataKey="value"
                            label={{ fill: '#2d3748', fontSize: 11, fontWeight: 'bold' }} 
                        >
                            {dadosConsolidados?.graficoDestino.map((entry, index) => <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />)}
                            <Label 
                                value={dadosConsolidados?.totalFora || 0} 
                                position="center" 
                                fill="#2d3748" 
                                style={{ fontSize: '20px', fontWeight: '800' }} 
                            />
                        </Pie>
                        <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontSize: '11px' }} />
                        <Legend layout="vertical" verticalAlign="middle" align="right" wrapperStyle={{ fontSize: '10px', lineHeight: '18px' }}/>
                    </PieChart>
                </ResponsiveContainer>
            </div>
        </div>

        {/* GRÁFICO 3: GRÊMIOS */}
        {dadosConsolidados?.graficoGremioEquipe && dadosConsolidados.graficoGremioEquipe.length > 0 && (
            <div className="interactive-card" style={{ backgroundColor: 'white', borderRadius: '12px', padding: '16px', border: '1px solid #e2e8f0' }}>
                <h3 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#805ad5', fontWeight: '700' }}>Grêmios / Equipes</h3>
                <div style={{ height: '180px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie 
                                data={dadosConsolidados.graficoGremioEquipe} 
                                cx="50%" cy="50%" 
                                innerRadius={45} outerRadius={70} 
                                paddingAngle={3} 
                                dataKey="value"
                                label={{ fill: '#2d3748', fontSize: 11, fontWeight: 'bold' }}
                            >
                                {dadosConsolidados.graficoGremioEquipe.map((entry, index) => <Cell key={`cell-gremio-${index}`} fill={PIE_COLORS[(index + 2) % PIE_COLORS.length]} />)}
                                <Label 
                                    value={totalGremiosEquipes} 
                                    position="center" 
                                    fill="#2d3748" 
                                    style={{ fontSize: '20px', fontWeight: '800' }} 
                                />
                            </Pie>
                            <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontSize: '11px' }} />
                            <Legend layout="vertical" verticalAlign="middle" align="right" wrapperStyle={{ fontSize: '10px', lineHeight: '18px' }}/>
                        </PieChart>
                    </ResponsiveContainer>
                </div>
            </div>
        )}

      </div>

      {/* 4. LAYOUT DIVIDIDO COMPACTO */}
      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
        
        {/* LADO ESQUERDO */}
        <div style={{ flex: '1 1 250px', backgroundColor: '#f8fafc', padding: '16px', borderRadius: '10px', border: '1px solid #e2e8f0', alignSelf: 'flex-start' }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#4a5568', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                RESUMO POR COMPANHIA
            </h3>
            
            {dadosConsolidados && Object.keys(dadosConsolidados.resumoCiaDestino)
                .sort((a,b) => a.localeCompare(b))
                .map(ciaName => (
                <div key={ciaName} style={{ marginBottom: '16px' }}>
                    <div style={{ fontWeight: '800', fontSize: '14px', color: '#2d3748', marginBottom: '8px' }}>
                        {ciaName}
                    </div>
                    {Object.entries(dadosConsolidados.resumoCiaDestino[ciaName])
                        .sort((a,b) => a[0].localeCompare(b[0]))
                        .map(([destino, count]) => (
                        <div key={destino} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#4a5568', marginBottom: '4px', borderBottom: '1px solid #edf2f7', paddingBottom: '4px' }}>
                            <span>{destino}</span>
                            <span style={{ fontWeight: '700', color: '#2d3748' }}>{count}</span>
                        </div>
                    ))}
                </div>
            ))}
        </div>

        {/* LADO DIREITO */}
        <div style={{ flex: '2 1 450px' }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#4a5568', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                MILITARES AFASTADOS (DETALHES)
            </h3>

            {dadosConsolidados && Object.values(dadosConsolidados.porCia)
                .sort((a,b) => a.nome.localeCompare(b.nome))
                .map(cia => (
                <div key={cia.nome} style={{ marginBottom: '20px' }}>
                    <div style={{ borderBottom: '2px solid #e2e8f0', paddingBottom: '6px', marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                     <span style={{ fontWeight: '800', fontSize: '15px', color: '#2d3748' }}>{cia.nome}</span>
                     <BotaoCopiarCia cia={cia} configDestinos={configDestinos} />
                    </div>

                    {Object.values(cia.pelotoes)
                        .sort((a,b) => a.nome.localeCompare(b.nome, 'pt-BR', { numeric: true }))
                        .map(pel => (
                            <AccordionPelotao key={pel.nome} pel={pel} configDestinos={configDestinos} />
                    ))}
                </div>
            ))}

            {dadosConsolidados && Object.keys(dadosConsolidados.porCia).length === 0 && (
                <div style={{ textAlign: 'center', padding: '30px', color: '#a0aec0', border: '1px dashed #cbd5e0', borderRadius: '10px' }}>
                    <p style={{margin: 0, fontWeight: '500'}}>Nenhum registro encontrado para os filtros selecionados.</p>
                </div>
            )}
        </div>

      </div>
    </div>
  );
};

const KPICard = ({ title, value, color, icon }) => (
    <div className="interactive-card" style={{ backgroundColor: 'white', padding: '12px 16px', borderRadius: '10px', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '12px', borderLeft: `4px solid ${color}` }}>
        <div style={{ backgroundColor: `${color}15`, padding: '10px', borderRadius: '10px', color: color }}>{icon}</div>
        <div>
            <div style={{ fontSize: '11px', color: '#718096', fontWeight: '700', textTransform: 'uppercase', marginBottom: '0px', letterSpacing: '0.5px' }}>{title}</div>
            <div style={{ fontSize: '22px', fontWeight: '800', color: '#2d3748', lineHeight: 1.2 }}>{value}</div>
        </div>
    </div>
);

export default ResultadoOnlineDashboard;